import { Box, ListItem, ListItemText, Tooltip, Typography } from '@mui/material'
import { Info, PhoneCall } from 'react-feather'
import React, { useMemo } from 'react'
import { chain } from 'mathjs'
import { useTheme } from '@mui/material/styles'
import useUsageReport from '../../api/quota/useUsageReport'
import { listItemStyle } from './UsageReportCard.styles'
import { listItemTextStyle } from './UsageReportCard.styles'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../utils/FormatDate'

function QuotaTooltip(props: { title: React.ReactNode; label: string }) {
	return (
		<Tooltip arrow placement="top" title={<Typography sx={{ fontSize: '14px' }}>{props.title}</Typography>}>
			<Box component="span" role="img" aria-label={props.label} sx={{ display: 'inline-flex', lineHeight: 0 }}>
				<Info style={{ width: '15px', height: '15px' }} />
			</Box>
		</Tooltip>
	)
}

function formatCount(value: number): string {
	return Math.round(value).toLocaleString('en-US')
}

function quotaLine(used: number, limit: number): string {
	return `${formatCount(used)} / ${formatCount(limit)}`
}

type UsageReportCardProps = {
	sidebarOpen: boolean
}

export default function UsageReportCard({ sidebarOpen }: UsageReportCardProps) {
	const { palette } = useTheme()
	const { t } = useTranslation()
	const { usage, isFetching } = useUsageReport()

	const dailyPercentage = useMemo(() => {
		if (!isFetching && usage && usage.max_daily_calls) {
			return `${chain(usage.daily_calls).divide(usage.max_daily_calls).multiply(100).round(0)}%`
		}
		return `${0}%`
	}, [isFetching, usage])

	const planPercentage = useMemo(() => {
		if (!isFetching && usage && usage.max_total_calls) {
			return `${chain(usage.total_calls).divide(usage.max_total_calls).multiply(100).round(0)}%`
		}
		return `${0}%`
	}, [isFetching, usage])

	const planPaused = !!usage && usage.max_total_calls > 0 && usage.total_calls >= usage.max_total_calls
	const unlockWhen = usage?.next_unlock_at ? formatDate(usage.next_unlock_at) : ''
	const unlockCount = usage?.next_unlock_calls ?? 0
	const hasNextUnlock = !!unlockWhen && unlockCount > 0
	const showFutureUnlock = !planPaused && hasNextUnlock
	const planTooltip = t('sidebar_menu.call_quota.plan_quota_i')
	const pausedCopy = hasNextUnlock
		? t('sidebar_menu.call_quota.paused_with_unlock', {
				calls: formatCount(unlockCount),
				when: unlockWhen,
			})
		: t('sidebar_menu.call_quota.paused_fixed')

	return (
		<>
			{!sidebarOpen && !!usage && (
				<ListItem sx={listItemStyle({ isOpened: false })}>
					<div className="usage-item-container">
						<PhoneCall style={{ color: palette.colors.green }} />
						<ListItemText sx={listItemTextStyle()}>
							<div>{t('sidebar_menu.call_quota.today')}</div>
							<div>{dailyPercentage}</div>
						</ListItemText>
						<ListItemText sx={listItemTextStyle()}>
							<div>{t('sidebar_menu.call_quota.collapsed_plan')}</div>
							<div>{planPercentage}</div>
						</ListItemText>
					</div>
				</ListItem>
			)}
			{sidebarOpen && !!usage && (
				<Box sx={{ marginBottom: '40px' }}>
					<ListItem sx={listItemStyle({ isOpened: true })}>
						<div className="usage-item-container usage-title">
							<ListItemText primary={t('sidebar_menu.call_quota.title')} />
						</div>
					</ListItem>
					<Box
						sx={{
							border: '1px solid ' + palette.sidebar.borderGray,
							margin: '0 14px',
							borderRadius: '4px',
							whiteSpace: 'normal',
						}}
					>
						<ListItem sx={listItemStyle({ isOpened: true })}>
							<div className="usage-item-container">
								<ListItemText className="report-details">
									<div>{t('sidebar_menu.call_quota.today')}</div>
									<div style={{ whiteSpace: 'nowrap' }}>
										{quotaLine(usage.daily_calls, usage.max_daily_calls)}
									</div>
								</ListItemText>
								<QuotaTooltip
									label={t('sidebar_menu.call_quota.today')}
									title={t('sidebar_menu.call_quota.daily_quota_i')}
								/>
								<div style={{ flex: 4 }} />
							</div>
						</ListItem>
						<ListItem sx={listItemStyle({ isOpened: true })}>
							<div className="usage-item-container">
								<ListItemText className="report-details">
									<div>{t('sidebar_menu.call_quota.plan_usage')}</div>
									<div style={{ whiteSpace: 'nowrap' }}>
										{quotaLine(usage.total_calls, usage.max_total_calls)}
									</div>
								</ListItemText>
								<QuotaTooltip label={t('sidebar_menu.call_quota.plan_usage')} title={planTooltip} />
								<div style={{ flex: 4 }} />
							</div>
						</ListItem>
						{(planPaused || showFutureUnlock) && (
							<Box sx={{ px: '12px', pb: '10px', whiteSpace: 'normal' }}>
								{planPaused && (
									<Typography
										sx={{
											fontSize: '12px',
											lineHeight: 1.35,
											color: palette.colors.warn,
											whiteSpace: 'normal',
										}}
									>
										{pausedCopy}
									</Typography>
								)}
								{showFutureUnlock && (
									<Typography
										sx={{
											fontSize: '12px',
											lineHeight: 1.35,
											color: palette.colors.green,
											whiteSpace: 'normal',
										}}
									>
										{t('sidebar_menu.call_quota.next_unlock', {
											calls: formatCount(unlockCount),
											when: unlockWhen,
										})}
									</Typography>
								)}
							</Box>
						)}
					</Box>
				</Box>
			)}
		</>
	)
}
